import { useState } from "react";
import useAxios from "@/hooks/useAxios";
import { ApiModules } from "@/types/enums/api-modules";
import { successToast } from "@/utils/toasts";
import { TokenHelper } from "@/utils/tokensHelper";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "@/types/response";

export type MentorshipRequest = {
  mentor_name: number;
  Start_Date: string;
  End_Date: string;
};

export type ChatMessage = {
  id: number;
  sender_id: string;
  message: string;
  created_at: string;
};

export const useMentorApi = () => {
  const { axios } = useAxios(ApiModules.MENTORAT);
  const [loading] = useState(false);

  const fetchMentorshipProgress = async () => {
    try {
      const res = await axios.get("/mentorship/progress/");
      return { success: true, data: res.data };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error("Erreur progression mentorat", error);
      return { success: false, error };
    }
  };

  const getMessages = async (mentoringId: string) => {
    try {
      const tokenHelper = new TokenHelper();
      const { access, refresh } = tokenHelper.getTokens();

      const res = await axios.get(`/mentoring/chat/${mentoringId}/messages/`, {
        headers: {
          Authorization: `Bearer ${access},${refresh}`,
        },
      });
      return { success: true, data: res.data as ChatMessage[] };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error("Erreur chargement messages", error);
      return { success: false, error };
    }
  };

  const sendMessage = async (mentoringId: string, message: string) => {
    try {
      const tokenHelper = new TokenHelper();
      const { access } = tokenHelper.getTokens();

      const res = await axios.post(
        `/mentoring/chat/${mentoringId}/messages/`,
        { message },
        {
          headers: {
            Authorization: `Bearer ${access}`,
          },
        },
      );
      return { success: true, data: res.data };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error("Erreur envoi message", error);
      return { success: false, error };
    }
  };

  const submitMentoringRequest = async (
    Nom_mentor: string,
    Start_Date: string,
    End_Date: string,
  ) => {
    try {
      const tokenHelper = new TokenHelper();
      const { access } = tokenHelper.getTokens();

      const res = await axios.post(
        "/mentoring/",
        { Nom_mentor, Start_Date, End_Date },
        {
          headers: {
            Authorization: `Bearer ${access}`,
          },
        },
      );

      successToast("Demande de mentorat envoyée");
      return { success: true, data: res.data };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error("Erreur envoi de la demande de mentorat", error);
      return { success: false, error };
    }
  };

  const getAllRequests = async () => {
    try {
      const res = await axios.get("/mentoring/");
      return { success: true, data: res.data };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error("Erreur demandes", error);
      return { success: false, error };
    }
  };

  const respondToRequest = async (
    requestId: string,
    status: "active" | "inactive",
  ) => {
    try {
      const tokenHelper = new TokenHelper();
      const { access } = tokenHelper.getTokens();
      console.log("Access Token:", access);

      const res = await axios.patch(
        `/mentoring/requests/${requestId}/respond/`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${access}`,
          },
        },
      );

      successToast("Demande mise à jour");
      return { success: true, data: res.data };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error("Failed to respond to request", error);
      return { success: false, error };
    }
  };

  const getAllMentorings = async () => {
    try {
      const tokenHelper = new TokenHelper();
      const { access } = tokenHelper.getTokens();
      console.log("Access Token:", access);

      const res = await axios.get("/mentoring/", {
        headers: {
          Authorization: `Bearer ${access}`,
        },
      });
      console.log("Mentorings Data:", res.data);
      return { success: true, data: res.data };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error("Erreur mentorings", error);
      return { success: false, error };
    }
  };

  const createMentor = async (
    Nom_mentor: string,
    Prenom_mentor: string,
    Profession: string,
    lastConnected: string,
  ) => {
    try {
      const tokenHelper = new TokenHelper();
      const { access } = tokenHelper.getTokens();

      const res = await axios.post(
        "/mentors/",
        { Nom_mentor, Prenom_mentor, Profession, lastConnected },
        {
          headers: {
            Authorization: `Bearer ${access}`,
          },
        },
      );

      successToast("Mentor créé avec succès");
      return { success: true, data: res.data };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error("Erreur création mentor", error);
      return { success: false, error };
    }
  };

  const getMentorList = async () => {
    try {
      const tokenHelper = new TokenHelper();
      const { access } = tokenHelper.getTokens();

      const res = await axios.get("/mentors/list/", {
        headers: {
          Authorization: `Bearer ${access}`,
        },
      });

      return { success: true, data: res.data };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error("Erreur récupération liste des mentors", error);
      return { success: false, error };
    }
  };

  return {
    loading,
    fetchMentorshipProgress,
    getMessages,
    sendMessage,
    submitMentoringRequest,
    getAllRequests,
    respondToRequest,
    getAllMentorings,
    createMentor,
    getMentorList,
  };
};
